import { Tier as SubscriptionsV1Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import {
  ContractStatus,
  Contract as ContractsV1,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import {
  Subscription,
  SubscriptionType,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

// Mirrors the `Status` field of the `ProductStatus` Salesforce platform event, derived by the
// backend from org suspension, tier, and latest contract state.
export type ProductStatus =
  "Suspended" | "Active" | "Active (Trialing)" | "Inactive";

// Reasons the status can't be resolved on the frontend. `org-not-loaded`, `loading`, and
// `contracts-unavailable` have no backend equivalent - the Go activity always has a fully loaded
// org and a successful (possibly empty) contracts read by the time it runs.
export type ProductStatusUnknownReason =
  "org-not-loaded" | "loading" | "contracts-unavailable" | "no-contract";

export type ProductStatusResult =
  | { kind: "resolved"; status: ProductStatus }
  | { kind: "unknown"; reason: ProductStatusUnknownReason };

export interface DeriveProductStatusInput {
  // RoleDetails.active; undefined means the org hasn't loaded yet, distinct from a loaded `false`.
  orgIsActive: boolean | undefined;
  // Free tier orgs never have a billing relationship to check, so contracts are never consulted -
  // normalising this per-card avoids passing either card's raw (and incompatible) Tier enum here.
  isFreeTier: boolean;
  contracts: ContractsV1[] | undefined;
  isContractsLoading: boolean;
  hasContractsError: boolean;
}

const resolved = (status: ProductStatus): ProductStatusResult => ({
  kind: "resolved",
  status,
});

const unknown = (reason: ProductStatusUnknownReason): ProductStatusResult => ({
  kind: "unknown",
  reason,
});

// activeContractStatuses are the contract states that still grant product access, matching the
// backend's ProductStatus derivation.
const activeContractStatuses = [
  ContractStatus.ACTIVATED,
  ContractStatus.PAST_DUE,
  ContractStatus.TRIALING,
];

// deriveProductStatus recomputes the Salesforce ProductStatus for an org from source, mirroring
// the backend's derivation:
//  1. Org suspension always wins - a suspended org is always "Suspended".
//  2. Free tier orgs have no billing relationship to check - they always have product access.
//  3. Premium/Enterprise tier orgs resolve from their latest contract: "Active (Trialing)" while
//     trialing, "Active" while activated/past due, otherwise "Inactive".
//
// This is a client-side recomputation of what the backend would derive right now, not a read of
// the value actually sent to Salesforce - see the tooltip copy in each card for the caveats
// (sync can be disabled, no Salesforce Team record yet, or a prior event failed).
export const deriveProductStatus = (
  input: DeriveProductStatusInput,
): ProductStatusResult => {
  if (input.orgIsActive === undefined) {
    return unknown("org-not-loaded");
  }
  if (!input.orgIsActive) {
    return resolved("Suspended");
  }
  if (input.isFreeTier) {
    return resolved("Active");
  }
  if (input.isContractsLoading) {
    return unknown("loading");
  }
  if (input.hasContractsError) {
    return unknown("contracts-unavailable");
  }
  const latestContract = input.contracts?.[0];
  if (!latestContract) {
    return unknown("no-contract");
  }
  if (latestContract.status === ContractStatus.TRIALING) {
    return resolved("Active (Trialing)");
  }
  if (activeContractStatuses.includes(latestContract.status)) {
    return resolved("Active");
  }
  return resolved("Inactive");
};

// deriveProductStatusForChargify adapts deriveProductStatus for the Chargify card. The
// support/v1 `Tier` enum (PREMIUM = 0, ENTERPRISE = 1) cannot express "free" - PREMIUM is its
// proto zero value - so free tier is read off SubscriptionType instead, matching the backend's
// rule that TIER_FREE is subscription_type null or FreeOrg
// (api-subscriptions/internal/subscriptions/retrieve.go).
export const deriveProductStatusForChargify = (
  orgIsActive: boolean | undefined,
  subscription: Subscription | undefined,
  contracts: ContractsV1[] | undefined,
  isContractsLoading: boolean,
  hasContractsError: boolean,
): ProductStatusResult =>
  deriveProductStatus({
    orgIsActive,
    isFreeTier:
      subscription?.type === SubscriptionType.FREE ||
      subscription?.type === SubscriptionType.FREE_ORG,
    contracts,
    isContractsLoading,
    hasContractsError,
  });

// deriveProductStatusForZuora adapts deriveProductStatus for the Zuora card, whose subscription
// carries a `Tier` from subscriptions/v1 (UNSPECIFIED = 0, FREE = 1, PREMIUM = 2, ENTERPRISE = 3)
// - a different enum to the Chargify card's, with clashing numeric values, so `Tier.FREE` is only
// ever read here, never passed into the shared core.
export const deriveProductStatusForZuora = (
  orgIsActive: boolean | undefined,
  tier: SubscriptionsV1Tier | undefined,
  contracts: ContractsV1[] | undefined,
  isContractsLoading: boolean,
  hasContractsError: boolean,
): ProductStatusResult =>
  deriveProductStatus({
    orgIsActive,
    isFreeTier: tier === SubscriptionsV1Tier.FREE,
    contracts,
    isContractsLoading,
    hasContractsError,
  });
